import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import {
  fetchLiveFacts,
  retireLiveFact,
  publishLiveFact,
  unpublishLiveFact,
  replaceLiveFact,
  moveLiveFact,
  canMoveLiveFact,
  liveFactDrawerLine,
  DocumentsError,
  DRAWER_LABELS,
  type LiveFact,
} from '@/lib/api'
import DrawerPicker from '@/components/DrawerPicker'
import { FactRecordingLine } from '@/components/VoiceAnswer'
import { FactFilePanel } from '@/components/FactFile'
import { type DrawerChoice } from '@/lib/drawer-choice'

/**
 * WHAT IT KNOWS — the live corpus, and the one verb that removes something.
 *
 * Until this screen existed, an approved fact disappeared from the dashboard
 * the moment it was signed: the review queue shows what is WAITING, the
 * re-confirmation card shows what is OVERDUE, and nothing showed what the
 * assistant was actually telling people. The only way to look was the D1
 * console, and the only way to take a fact back was a hand-written UPDATE
 * against live customer data.
 *
 * ONE SCREEN, TWO PRODUCTS (batch 2e). Written for Foredesk's dashboard and
 * moved here unchanged so Kelvo's owner gets the same screen rather than a
 * copy of it — the rule DocumentIntake and QuestionQueue already follow.
 * Foredesk renders it with no props at all and sees exactly what it always
 * did. Kelvo passes:
 *
 *   drawers     — each fact shows its drawer and who that reaches, facts with
 *                 no file behind them can be moved, and the list opens at a
 *                 fact named in the address (#fact-<id>, from an Answered card)
 *   publishing  — false on Kelvo: there is no public page, so "Put on my
 *                 public page" would promise something that does not happen.
 *                 TAKING OFF stays available whatever this says: removing a
 *                 sentence from the open web must never be one click further
 *                 away than the mistake it corrects.
 *   audience    — whose words the card uses ("customers" or "staff")
 */
export function LiveCorpusSection({
  heading = 'What your assistant knows',
  intro = (
    <>
      Everything you have approved. This is what customers can be told — nothing else. If one of
      these has stopped being true, take it out and it stops being said straight away. Anything
      you put on your public page is readable by anyone, and shows the date you confirmed it.
    </>
  ),
  emptyMessage = 'Nothing yet. Approve something in Documents or Questions and it will appear here.',
  drawers = false,
  publishing = true,
  audience = 'customers',
}: {
  heading?: string
  intro?: ReactNode
  emptyMessage?: string
  drawers?: boolean
  publishing?: boolean
  audience?: 'customers' | 'staff'
}) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [facts, setFacts] = useState<LiveFact[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  // The fact named in the address, once it has been found and shown. Kept so
  // the highlight survives the reload that follows any change on the page.
  const [focused, setFocused] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const token = await getTokenRef.current()
      const corpus = await fetchLiveFacts(token)
      setFacts(corpus.facts)
      setTruncated(corpus.truncated)
    } catch {
      setLoadError('Could not load your facts.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // Kelvo: open at the fact an Answered card linked to. Once, after the first
  // load that contains it; a fact not on the page (taken out since, or past the
  // first 200) is simply not scrolled to.
  useEffect(() => {
    if (!drawers || facts === null || focused !== null) return
    const wanted = factIdFromHash(window.location.hash)
    if (!wanted || !facts.some((f) => f.id === wanted)) return
    setFocused(wanted)
    document.getElementById(`fact-${wanted}`)?.scrollIntoView({ block: 'center' })
  }, [drawers, facts, focused])

  const silent = (facts ?? []).filter((f) => !f.answering).length
  const onPage = (facts ?? []).filter((f) => f.publishedAt !== null).length
  const unfiled = drawers ? (facts ?? []).filter((f) => f.drawer === null).length : 0

  return (
    <section className="mt-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight">{heading}</h1>
      <p className="mt-1 text-sm text-text-muted">{intro}</p>

      {loadError ? (
        <p className="mt-4 text-sm text-text-danger">
          {loadError}{' '}
          <button onClick={() => void load()} className="underline underline-offset-2">
            Try again
          </button>
        </p>
      ) : facts === null ? (
        <p className="mt-4 text-text-muted">Loading…</p>
      ) : facts.length === 0 ? (
        <p className="mt-4 text-text-muted">{emptyMessage}</p>
      ) : (
        <>
          <p className="mt-4 text-sm text-text-muted">
            {facts.length === 1 ? '1 fact' : `${facts.length} facts`}
            {silent > 0
              ? ` · ${silent === 1 ? '1 is not being used' : `${silent} are not being used`}`
              : ''}
            {/* Counted, because "how much of my corpus is public?" is a question
                the owner should never have to answer by scrolling. */}
            {onPage > 0 ? ` · ${onPage} on your public page` : ''}
          </p>
          {unfiled > 0 ? (
            // Kelvo: said once, above the list, while any fact is in no drawer.
            // Only the owner is told what is in it, which is safe, but it is
            // also out of reach of the staff it was probably meant for.
            <p
              role="status"
              className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3 text-sm text-text-primary"
            >
              {unfiled === 1 ? '1 fact is' : `${unfiled} facts are`} not in a drawer. Only you are
              told {unfiled === 1 ? 'it' : 'them'} until you choose a drawer.
            </p>
          ) : null}
          {truncated ? (
            <p className="mt-1 text-sm text-text-muted">
              Showing the 200 most recent. You have more than that.
            </p>
          ) : null}
          <ul className="mt-4 stack-y-4">
            {facts.map((f) => (
              <li key={f.id}>
                <LiveFactCard
                  fact={f}
                  onRetired={load}
                  drawers={drawers}
                  publishing={publishing}
                  audience={audience}
                  focused={focused === f.id}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

/** "#fact-<id>" to "<id>"; anything else to null. Ids are the engine's own. */
function factIdFromHash(hash: string): string | null {
  const match = /^#fact-([A-Za-z0-9-]{1,64})$/.exec(hash)
  return match ? match[1]! : null
}

function LiveFactCard({
  fact,
  onRetired,
  drawers,
  publishing,
  audience,
  focused,
}: {
  fact: LiveFact
  onRetired: () => void
  drawers: boolean
  publishing: boolean
  audience: 'customers' | 'staff'
  focused: boolean
}) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [confirming, setConfirming] = useState(false)
  // Publishing gets its own confirm step; unpublishing does not. They are not
  // symmetrical acts: putting a sentence on the open web is effectively
  // irreversible once it has been read, cached or crawled, while taking it down
  // is the safe direction and should never be one click further away than the
  // mistake it corrects.
  const [confirmingPublish, setConfirmingPublish] = useState(false)
  const [editing, setEditing] = useState(false)
  const [moving, setMoving] = useState(false)
  const [target, setTarget] = useState<DrawerChoice>(fact.drawer ?? '')
  const [draft, setDraft] = useState(fact.content)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The two verbs are mutually exclusive on screen: a card offering "take this
  // out" and "save the new wording" at the same moment invites doing both.
  const unchanged = draft.trim() === fact.content.trim()
  const movable = drawers && canMoveLiveFact(fact)

  function retire() {
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        await retireLiveFact(await getTokenRef.current(), fact.id)
        onRetired()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That did not save. Try again.')
      } finally {
        // Same finally, for the same reason, even though the bug cannot be SEEN
        // here: a retired fact leaves the list, so the stuck state unmounts
        // before anyone reads it. That is luck, not correctness — the day this
        // card survives its own success (a failed reload, a list that keeps
        // retired rows) the identical defect appears, and it would look new.
        setBusy(false)
        setConfirming(false)
      }
    })()
  }

  function setPublished(next: boolean) {
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        const token = await getTokenRef.current()
        // The act is named at the call site rather than computed from a
        // boolean the card last rendered — a stale card cannot publish
        // something the owner meant to withdraw.
        if (next) await publishLiveFact(token, fact.id)
        else await unpublishLiveFact(token, fact.id)
        onRetired()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That did not save. Try again.')
      } finally {
        // CLEARED IN A FINALLY, NOT ON THE FAILURE PATH ONLY. The first version
        // reset these in the catch alone, on the assumption — inherited from
        // retire() below — that a successful mutation makes the card disappear.
        // That holds for retirement, which removes the fact from the list. It
        // does NOT hold here: a published fact stays on screen, so the card
        // re-rendered still saying "Publishing…" over a write that had already
        // committed. Found in production on the first real publish.
        //
        // The owner reads that as "it did not work", and the one thing he must
        // never be uncertain about is whether a sentence is now on the open web.
        setBusy(false)
        setConfirmingPublish(false)
      }
    })()
  }

  function save() {
    if (unchanged || !draft.trim()) return
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        const res = await replaceLiveFact(await getTokenRef.current(), fact.id, {
          content: draft.trim(),
        })
        if (!res.embedded) {
          // Told, not hidden. The replacement is live in the record but silent
          // in answers, and the owner should know that before he assumes the
          // correction is in front of customers.
          setError('Saved, but it is not being used in answers yet. It should sort itself out.')
        }
        onRetired()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'That did not save. Try again.')
      } finally {
        // Third instance of the same shape. save() replaces the fact's wording,
        // which DOES keep the card on screen — so this one could stick exactly
        // as publish did, and only happened not to because the reload usually
        // wins the race.
        setBusy(false)
      }
    })()
  }

  /**
   * Move it (Kelvo). '' (nothing chosen) cannot be sent; the button is
   * disabled until a real choice differs from where it is. On a 409 the fact
   * moved elsewhere a moment ago, or a file now stands behind it: the engine's
   * sentence is shown and the list reloads, so the card shows the truth.
   * Cleared in a finally, the shape the three verbs above learned.
   */
  function move() {
    if (target === '' || target === (fact.drawer ?? null)) return
    setBusy(true)
    setError(null)
    void (async () => {
      try {
        await moveLiveFact(await getTokenRef.current(), fact.id, target)
        setMoving(false)
        onRetired()
      } catch (e) {
        setError(e instanceof DocumentsError ? e.message : 'That did not move. Try again.')
        if (e instanceof DocumentsError && e.status === 409) onRetired()
      } finally {
        setBusy(false)
      }
    })()
  }

  const drawerLine = drawers ? liveFactDrawerLine(fact.drawer ?? null) : null
  const told = audience === 'staff' ? 'staff' : 'customers'

  return (
    <div
      id={`fact-${fact.id}`}
      className={`rounded-card border bg-surface-raised p-5 ${
        focused ? 'border-brand-accent ring-2 ring-brand-accent' : 'border-border-subtle'
      }`}
    >
      <p className="text-text-primary">{fact.content}</p>
      <p className="mt-1 text-xs text-text-muted">
        {fact.sourceDoc ? `From "${fact.sourceDoc}"` : sourceLabel(fact.sourceType)}
      </p>

      {drawerLine ? (
        <p className={`mt-1 text-xs ${drawerLine.unfiled ? 'font-medium text-text-danger' : 'text-text-muted'}`}>
          {drawerLine.text}
        </p>
      ) : null}

      {/* Build 10b: the recording behind an answer spoken on the Questions
          page. Only when the engine sent one — a pod with voice answers — so
          Foredesk's card is unchanged. */}
      {fact.voiceNote ? <FactRecordingLine recording={fact.voiceNote} onChanged={onRetired} /> : null}

      {/* G46: the kept file this answer offers staff to open. Only when the
          engine sent the key — a pod that keeps files, an answer the owner
          wrote — so every other card is unchanged. */}
      {fact.attachment !== undefined ? (
        <FactFilePanel factId={fact.id} attachment={fact.attachment} onChanged={onRetired} />
      ) : null}

      {/* Kelvo: a fact with a file behind it moves with the file, and the card
          says so rather than offering a move the engine would refuse. Deleting
          a file retires its facts (A21), so the second branch is a lock that
          should never show; if it ever does, it says what is true. */}
      {drawers && fact.fromDocument ? (
        fact.fromDocument.kept ? (
          <p className="mt-1 text-xs text-text-muted">
            Moves with its file, “{fact.fromDocument.name}”.{' '}
            <Link to="/documents" className="text-text-primary underline underline-offset-2">
              Move the file on Documents
            </Link>
          </p>
        ) : (
          <p className="mt-1 text-xs text-text-muted">
            Its file, “{fact.fromDocument.name}”, was deleted, so this stays in{' '}
            {fact.drawer ? DRAWER_LABELS[fact.drawer] : 'no drawer'} for now. If you change its
            wording, the new wording can be moved.
          </p>
        )
      ) : null}

      {fact.publishedAt !== null ? (
        <p className="mt-2 text-xs text-text-muted">
          On your public page since {formatPublishedDate(fact.publishedAt)}.
        </p>
      ) : null}

      {/* An approved fact with no vector reaches nobody. It is the owner's fact
          and it is on no other screen, so it is shown and named rather than
          quietly dropped from the list. */}
      {!fact.answering ? (
        <p className="mt-2 text-xs text-text-muted">
          Not being used in answers yet. Nothing you did wrong — ask us to look if it stays this
          way.
        </p>
      ) : null}

      {editing ? (
        <div className="mt-3">
          <label htmlFor={`edit-${fact.id}`} className="text-sm text-text-muted">
            Say it the way it should read now
          </label>
          <textarea
            id={`edit-${fact.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            disabled={busy}
            className="mt-1.5 w-full rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-text-primary focus:border-brand-accent focus:outline-hidden"
          />
          <p className="mt-1 text-xs text-text-muted">
            The old wording is kept, so you can always see what {told} used to be told.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={save}
              disabled={busy || unchanged || !draft.trim()}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy ? 'Saving…' : 'Save the new wording'}
            </button>
            <button
              onClick={() => {
                setDraft(fact.content)
                setEditing(false)
                setError(null)
              }}
              disabled={busy}
              className="text-sm text-text-muted underline underline-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : moving ? (
        <div className="mt-3">
          <DrawerPicker
            id={`move-fact-${fact.id}`}
            label="Move it to"
            hint="Your staff are told it only if their role reaches that drawer."
            value={target}
            onChange={setTarget}
            disabled={busy}
            allowUnfiled
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={move}
              disabled={busy || target === '' || target === (fact.drawer ?? null)}
              aria-busy={busy}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy ? 'Moving…' : 'Move'}
            </button>
            <button
              onClick={() => {
                setMoving(false)
                setTarget(fact.drawer ?? '')
                setError(null)
              }}
              disabled={busy}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : confirmingPublish ? (
        <div className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3">
          <p className="text-sm text-text-primary">
            Put this on your public page? Anyone will be able to read it, and search engines may
            keep a copy. It will show the date you last confirmed it.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={() => setPublished(true)}
              disabled={busy}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy ? 'Publishing…' : 'Yes, put it on my page'}
            </button>
            <button
              onClick={() => setConfirmingPublish(false)}
              disabled={busy}
              className="text-sm text-text-muted underline underline-offset-2"
            >
              Not yet
            </button>
          </div>
        </div>
      ) : confirming ? (
        <div className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3">
          <p className="text-sm text-text-primary">
            Take this out? Your assistant stops saying it straight away. You keep a record of it,
            and you can always add it again later.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={retire}
              disabled={busy}
              className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {busy ? 'Taking it out…' : 'Yes, take it out'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={busy}
              className="text-sm text-text-muted underline underline-offset-2"
            >
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-text-primary underline underline-offset-2"
          >
            Change the wording
          </button>
          {movable ? (
            <button
              onClick={() => {
                setTarget(fact.drawer ?? '')
                setError(null)
                setMoving(true)
              }}
              disabled={busy}
              className="text-sm text-text-primary underline underline-offset-2"
            >
              {fact.drawer === null ? 'Choose a drawer' : 'Move to another drawer'}
            </button>
          ) : null}
          {fact.publishedAt === null ? (
            publishing ? (
              <button
                onClick={() => setConfirmingPublish(true)}
                disabled={busy}
                className="text-sm text-text-primary underline underline-offset-2"
              >
                Put on my public page
              </button>
            ) : null
          ) : (
            <button
              onClick={() => setPublished(false)}
              disabled={busy}
              className="text-sm text-text-primary underline underline-offset-2"
            >
              {busy ? 'Taking it off…' : 'Take off my public page'}
            </button>
          )}
          <button
            onClick={() => setConfirming(true)}
            className="text-sm text-text-danger underline underline-offset-2"
          >
            Take this out
          </button>
        </div>
      )}

      {error ? (
        <p role="alert" className="mt-2 text-sm text-text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The date a fact went onto the public page, as the owner would say it.
 *
 * Absolute, like the date on the page itself and for the same reason: "3 weeks
 * ago" stops being true the moment the screen is left open, and this is the one
 * number that tells him how long a sentence has been readable by strangers.
 */
function formatPublishedDate(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** Where a fact came from, in the owner's words rather than the schema's. */
function sourceLabel(sourceType: string): string {
  if (sourceType === 'voice') return 'From something you recorded'
  if (sourceType === 'gap_answer') return 'From a question you answered'
  if (sourceType === 'typed') return 'You typed this in'
  return 'From a document'
}
